import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const Register = () => {
  const verifyEmail = useLocation().state?.verifyEmail; // set when Login redirects an unverified user
  const [step, setStep] = useState(verifyEmail ? 2 : 1); // 1 = form, 2 = OTP
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '', department: '', regionLevel: '', website: '' });
  const [captcha, setCaptcha] = useState({ question: '', token: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [email, setEmail] = useState(verifyEmail || ''); // confirmed email after step 1
  const [otp, setOtp] = useState(['', '', '', '', '', '']); // 6 digits
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(verifyEmail ? 60 : 0);
  const otpRefs = useRef([]);
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  // Each captcha token is single-use, so fetch a fresh one after every submit
  const loadCaptcha = async () => {
    setCaptchaAnswer('');
    try {
      const res = await fetch(`${API_BASE}/auth/captcha`);
      setCaptcha(await res.json());
    } catch {
      setCaptcha({ question: '', token: '' });
    }
  };

  useEffect(() => { loadCaptcha(); }, []);

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(v => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error('Please fill in all required fields'); return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    if (!captchaAnswer.trim()) {
      toast.error('Please answer the verification question'); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, captchaToken: captcha.token, captchaAnswer })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed');
      setEmail(form.email);
      setStep(2);
      setResendCooldown(60);
      if (data.emailSent === false) toast.error(data.message);
      else toast.success('Verification code sent to your email!');
    } catch (err) {
      toast.error(err.message);
    }
    loadCaptcha();
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { toast.error('Please enter the complete 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Verification failed');
      // Auto-login
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user.workspace) {
        localStorage.setItem('activeWorkspaceId', data.user.workspace.id);
      }
      toast.success('Email verified! Welcome to EpiSurveillance 🎉');
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.message);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to resend');
      toast.success('New code sent!');
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      toast.error(err.message);
    }
    setResending(false);
  };

  const inp = `reg-input`;
  const lab = { display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#020617', padding: 20, position: 'relative', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @keyframes pulse-grid { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.07; } }
        .reg-input { width: 100%; padding: 14px 18px; border: 2px solid #1e293b; border-radius: 12px; font-size: 14px; outline: none; background: #0f172a; color: #f1f5f9; transition: border 0.3s; font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }
        .reg-input:focus { border-color: #00d4aa; }
        .reg-input::placeholder { color: #475569; }
        .otp-digit { width: 52px; height: 64px; border: 2px solid #1e293b; border-radius: 14px; font-size: 28px; font-weight: 800; text-align: center; outline: none; background: #0f172a; color: #f1f5f9; transition: all 0.2s; font-family: 'DM Serif Display', serif; -moz-appearance: textfield; }
        .otp-digit::-webkit-outer-spin-button, .otp-digit::-webkit-inner-spin-button { -webkit-appearance: none; }
        .otp-digit:focus { border-color: #00d4aa; box-shadow: 0 0 0 3px rgba(0,212,170,0.15); }
        .otp-digit.filled { border-color: #00d4aa; background: rgba(0,212,170,0.05); color: #00d4aa; }
      `}</style>

      {/* Background */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(0,212,170,0.08) 1px, transparent 1px)', backgroundSize: '36px 36px', animation: 'pulse-grid 4s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', top: '5%', right: '15%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,212,170,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <div style={{ position: 'absolute', bottom: '10%', left: '10%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 70%)', filter: 'blur(50px)' }} />

      <div style={{ width: '100%', maxWidth: step === 1 ? 540 : 480, position: 'relative', zIndex: 2 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, color: '#020617', fontFamily: "'DM Serif Display', serif" }}>ES</div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>EpiSurveillance</div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '2px', color: '#00d4aa', textTransform: 'uppercase' }}>Modern Disease Surveillance</div>
            </div>
          </Link>
        </div>

        {step === 1 ? (
          /* ── STEP 1: Registration Form ── */
          <div className="auth-card" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 24, padding: '44px 40px', border: '1px solid #1e293b' }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#f1f5f9', marginBottom: 6, textAlign: 'center' }}>Create Free Account</h2>
            <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', marginBottom: 32 }}>Sign up instantly — verify your email to get viewer access</p>

            <form onSubmit={handleRegister}>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={lab}>First Name *</label>
                  <input placeholder="John" className={inp} value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                </div>
                <div>
                  <label style={lab}>Last Name *</label>
                  <input placeholder="Smith" className={inp} value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Email *</label>
                <input type="email" placeholder="you@organization.gov" className={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Password *</label>
                <input type="password" placeholder="Min 6 characters" className={inp} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={lab}>Phone</label>
                  <input placeholder="(540) 322-7406" className={inp} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label style={lab}>Department</label>
                  <input placeholder="Epidemiology" className={inp} value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} />
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={lab}>Region Level</label>
                <select className={inp} style={{ cursor: 'pointer' }} value={form.regionLevel} onChange={e => setForm({ ...form, regionLevel: e.target.value })}>
                  <option value="">Select Level</option>
                  <option value="international">International</option>
                  <option value="national">National</option>
                  <option value="state">State</option>
                  <option value="local">Local</option>
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={lab}>{captcha.question || 'Loading verification question...'} *</label>
                <input inputMode="numeric" placeholder="Your answer" className={inp} value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value.replace(/D/g, ''))} required />
              </div>

              {/* Honeypot: hidden from people, bots that fill every field get silently dropped */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></label>
              </div>

              <div style={{ background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>
                ✅ You'll get <strong style={{ color: '#00d4aa' }}>instant Viewer access</strong> after email verification. Explore the full platform demo immediately.
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: 16, border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#334155' : 'linear-gradient(135deg, #00d4aa, #00b894)', color: loading ? '#94a3b8' : '#020617',
                fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.3s',
              }}>
                {loading ? 'Creating Account...' : 'Sign Up Free →'}
              </button>
            </form>

            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 14, color: '#64748b' }}>
              Already have access?{' '}
              <Link to="/login" style={{ color: '#00d4aa', fontWeight: 700, textDecoration: 'none' }}>Sign In</Link>
            </div>
          </div>
        ) : (
          /* ── STEP 2: OTP Verification ── */
          <div className="auth-card" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 24, padding: '44px 40px', border: '1px solid #1e293b', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(0,212,170,0.15), rgba(0,212,170,0.05))', border: '2px solid rgba(0,212,170,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 32 }}>📧</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Check Your Email</h2>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>We sent a 6-digit verification code to</p>
            <p style={{ color: '#00d4aa', fontWeight: 700, fontSize: 15, marginBottom: 32 }}>{email}</p>

            <form onSubmit={handleVerify}>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 32 }}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    className={`otp-digit${digit ? ' filled' : ''}`}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    onFocus={e => e.target.select()}
                  />
                ))}
              </div>

              <button type="submit" disabled={loading || otp.join('').length < 6} style={{
                width: '100%', padding: 16, border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: (loading || otp.join('').length < 6) ? 'not-allowed' : 'pointer',
                background: (loading || otp.join('').length < 6) ? '#334155' : 'linear-gradient(135deg, #00d4aa, #00b894)',
                color: (loading || otp.join('').length < 6) ? '#94a3b8' : '#020617',
                fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.3s', marginBottom: 20
              }}>
                {loading ? 'Verifying...' : 'Verify Email →'}
              </button>
            </form>

            <div style={{ fontSize: 14, color: '#64748b' }}>
              Didn't receive it?{' '}
              <button
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
                style={{ background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'default' : 'pointer', color: resendCooldown > 0 ? '#475569' : '#00d4aa', fontWeight: 700, fontSize: 14, padding: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>

            <button
              onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: 13, marginTop: 16, padding: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              ← Use a different email
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 28, fontSize: 13 }}>
          <Link to="/" style={{ color: '#475569', textDecoration: 'none' }}>← Back to EpiSurveillance Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
